import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WifiIcon from "@material-ui/icons/Wifi";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { StatusChip, tNegocio } from "./shared";
import { useStyles } from "./styles";
import { CodeLookupDialog } from "./CodeLookupDialog";

const emptyBox = () => ({
  id: null,
  name: "",
  host: "imap.gmail.com",
  port: 993,
  secure: true,
  user: "",
  password: "",
  enabled: true,
});

export default function CodeLookupSettings() {
  const classes = useStyles();
  const [boxes, setBoxes] = useState([]);
  const [products, setProducts] = useState([]);
  const [lookup, setLookup] = useState({ productId: "", email: "" });
  const [lookupOpen, setLookupOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: imap }, { data: prod }] = await Promise.all([
        api.get("/negocio/imap"),
        api.get("/negocio/products"),
      ]);
      setBoxes(imap.records || []);
      setProducts(prod.records || []);
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveBox = async (box) => {
    try {
      const payload = { ...box, port: Number(box.port || 993) };
      if (box.id) await api.put(`/negocio/imap/${box.id}`, payload);
      else await api.post("/negocio/imap", payload);
      toast.success(tNegocio("toastSaved"));
      await load();
    } catch (error) {
      toastError(error);
    }
  };

  const removeBox = async (box) => {
    try {
      await api.delete(`/negocio/imap/${box.id}`);
      toast.success(tNegocio("toastSaved"));
      await load();
    } catch (error) {
      toastError(error);
    }
  };

  const imapBoxes = boxes.filter((b) => b.kind !== "yopmail");
  const yopBoxes = boxes.filter((b) => b.kind === "yopmail");
  const drafts = imapBoxes.length >= 4 ? imapBoxes : [...imapBoxes, emptyBox()];
  const yopDrafts = yopBoxes.length >= 4 ? yopBoxes : [...yopBoxes, { id: null, kind: "yopmail", user: "", enabled: true }];

  return (
    <div>
      <div className={classes.captionBlock} style={{ marginBottom: 16 }}>
        {tNegocio("pages.codes.imapHint")}
      </div>
      <div className={classes.imapGrid}>
        {drafts.map((box, index) => (
          <ImapCard
            key={box.id || `new-${index}`}
            box={box}
            index={index}
            canAdd={!box.id && imapBoxes.length < 4}
            onSave={saveBox}
            onDelete={removeBox}
            onReload={load}
          />
        ))}
      </div>
      <div className={classes.sectionTitle}>{tNegocio("pages.codes.yopTitle")}</div>
      <div className={classes.captionBlock} style={{ marginBottom: 16 }}>
        {tNegocio("pages.codes.yopHint")}
      </div>
      <div className={classes.imapGrid}>
        {yopDrafts.map((box, index) => (
          <YopmailCard
            key={box.id || `yop-new-${index}`}
            box={box}
            index={index}
            onSave={saveBox}
            onDelete={removeBox}
            onReload={load}
          />
        ))}
      </div>
      <div className={classes.imapOperatorCard}>
        <div className={classes.sectionTitle} style={{ marginTop: 0 }}>
          {tNegocio("pages.codes.operatorTitle")}
        </div>
        <div className={classes.captionBlock} style={{ marginBottom: 12 }}>
          {tNegocio("pages.codes.operatorHint")}
        </div>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("cols.product")}
          value={lookup.productId}
          onChange={(e) => setLookup((s) => ({ ...s, productId: e.target.value }))}
        >
          {products.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.codes.email")}
          value={lookup.email}
          onChange={(e) => setLookup((s) => ({ ...s, email: e.target.value }))}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={!lookup.productId || !lookup.email}
          onClick={() => setLookupOpen(true)}
          style={{ marginTop: 12 }}
        >
          {tNegocio("pages.codes.lookup")}
        </Button>
      </div>
      <CodeLookupDialog
        open={lookupOpen}
        onClose={() => setLookupOpen(false)}
        productId={lookup.productId}
        email={lookup.email}
      />
    </div>
  );
}

function ImapCard({ box, index, canAdd, onSave, onDelete, onReload }) {
  const classes = useStyles();
  const [form, setForm] = useState(box);
  const [showPass, setShowPass] = useState(false);
  const [testing, setTesting] = useState(false);
  const [details, setDetails] = useState([]);
  useEffect(() => {
    setForm(box);
    setDetails([]);
  }, [box]);
  const patch = (next) => setForm((f) => ({ ...f, ...next }));
  const status = form.enabled === false ? "disabled" : "active";
  const cardTone = form.lastError ? classes.imapCardErr : form.lastOkAt ? classes.imapCardOk : "";

  const testBox = async () => {
    if (!form.user) {
      toast.error(tNegocio("pages.codes.userRequired"));
      return;
    }
    if (!form.password && !form.passwordSet) {
      toast.error(tNegocio("pages.codes.passwordRequired"));
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post(form.id ? `/negocio/imap/${form.id}/test` : "/negocio/imap/test", {
        id: form.id,
        host: form.host,
        port: Number(form.port || 993),
        user: form.user,
        password: form.password || "",
        secure: form.secure !== false,
      });
      const count = Number(data?.server_info?.messages || 0);
      setDetails(data?.details || []);
      toast.success(
        count
          ? `${tNegocio("pages.codes.imapOk")} · ${tNegocio("pages.codes.messagesCount").replace("{n}", String(count))}`
          : tNegocio("pages.codes.imapOk")
      );
      if (form.id) await onReload();
    } catch (error) {
      const extra = error?.response?.data?.details;
      if (Array.isArray(extra)) setDetails(extra);
      toastError(error);
      if (form.id) await onReload();
    } finally {
      setTesting(false);
    }
  };

  const toggleEnabled = (checked) => {
    const next = { ...form, enabled: checked };
    patch({ enabled: checked });
    if (form.id) onSave({ ...next, password: "" });
  };

  return (
    <div className={`${classes.imapCard} ${cardTone}`.trim()}>
      <div className={classes.imapCardHead}>
        <div className={classes.imapCardIdentity}>
          <span className={classes.imapCardIcon}>
            <MailOutlineIcon />
          </span>
          <div>
            <div className={classes.imapCardTitle}>
              {tNegocio("pages.codes.server")} {index + 1}
            </div>
            <div className={classes.imapCardEmail}>
              {form.user || tNegocio("pages.codes.userPlaceholder")}
            </div>
          </div>
        </div>
        <div className={classes.imapCardToggle}>
          <StatusChip value={status} />
          <Switch
            color="primary"
            size="small"
            checked={form.enabled !== false}
            onChange={(e) => toggleEnabled(e.target.checked)}
          />
        </div>
      </div>
      <div className={classes.imapCardBody}>
        {form.lastOkAt && !form.lastError ? (
          <div className={classes.imapHint} style={{ color: "#15803d", marginTop: 0 }}>
            <CheckCircleIcon style={{ color: "#16a34a" }} />
            {tNegocio("pages.codes.lastOk")}
          </div>
        ) : null}
        {form.lastError ? (
          <div className={classes.imapHint} style={{ color: "#c62828", marginTop: 0 }}>
            <InfoOutlinedIcon style={{ color: "#c62828" }} />
            {form.lastError}
          </div>
        ) : null}
        <div className={classes.imapHostRow}>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("pages.codes.host")}
            value={form.host || ""}
            onChange={(e) => patch({ host: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="number"
            label={tNegocio("pages.codes.portHint")}
            value={form.port || 993}
            onChange={(e) => patch({ port: e.target.value })}
          />
        </div>
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.codes.user")}
          value={form.user || ""}
          onChange={(e) => patch({ user: e.target.value, name: form.name || e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          type={showPass ? "text" : "password"}
          label={tNegocio("pages.codes.passwordApp")}
          placeholder={form.passwordSet ? "••••••••••" : ""}
          value={form.password || ""}
          onChange={(e) => patch({ password: e.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {form.passwordSet && !form.password ? (
                  <Chip
                    size="small"
                    label={tNegocio("pages.codes.passwordConfigured")}
                    className={classes.imapChipOk}
                    style={{ marginRight: 4 }}
                  />
                ) : null}
                <IconButton size="small" onClick={() => setShowPass((v) => !v)}>
                  {showPass ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <div className={classes.imapHint}>
          <InfoOutlinedIcon />
          {form.passwordSet
            ? tNegocio("pages.codes.passwordHint")
            : tNegocio("pages.codes.passwordHintNew")}
        </div>
        <FormControlLabel
          control={<Switch color="primary" checked={form.secure !== false} onChange={(e) => patch({ secure: e.target.checked })} />}
          label={tNegocio("pages.codes.secure")}
        />
        {details.length ? <div className={classes.imapDetails}>{details.join("\n")}</div> : null}
        <div className={classes.imapFooter}>
          <Button
            color="primary"
            variant="contained"
            className={classes.imapTestBtn}
            startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <WifiIcon />}
            onClick={testBox}
            disabled={testing || (!canAdd && !form.id && !form.user)}
          >
            {testing ? tNegocio("pages.codes.testing") : tNegocio("pages.codes.test")}
          </Button>
          <span className={classes.captionInline}>{tNegocio("pages.codes.testHint")}</span>
          <span style={{ flex: 1 }} />
          <Button color="primary" variant="outlined" onClick={() => onSave(form)} disabled={!canAdd && !form.id}>
            {tNegocio("save")}
          </Button>
          {form.id ? (
            <Tooltip title={tNegocio("pages.codes.removeMailbox")}>
              <IconButton onClick={() => onDelete(form)}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function YopmailCard({ box, index, onSave, onDelete, onReload }) {
  const classes = useStyles();
  const [form, setForm] = useState(box);
  const [testing, setTesting] = useState(false);
  const [details, setDetails] = useState([]);
  useEffect(() => {
    setForm(box);
    setDetails([]);
  }, [box]);
  const status = form.enabled === false ? "disabled" : "active";
  const cardTone = form.lastError ? classes.imapCardErr : form.lastOkAt ? classes.imapCardOk : "";

  const testBox = async () => {
    if (!form.user) {
      toast.error(tNegocio("pages.codes.yopRequired"));
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post(form.id ? `/negocio/imap/${form.id}/test` : "/negocio/imap/test", {
        id: form.id,
        kind: "yopmail",
        user: form.user,
      });
      setDetails(data?.details || []);
      toast.success(tNegocio("pages.codes.yopOk"));
      if (form.id) await onReload();
    } catch (error) {
      const extra = error?.response?.data?.details;
      if (Array.isArray(extra)) setDetails(extra);
      toastError(error);
      if (form.id) await onReload();
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className={`${classes.imapCard} ${cardTone}`.trim()}>
      <div className={classes.imapCardHead}>
        <div className={classes.imapCardIdentity}>
          <span className={classes.imapCardIcon}>
            <MailOutlineIcon />
          </span>
          <div>
            <div className={classes.imapCardTitle}>Yopmail {index + 1}</div>
            <div className={classes.imapCardEmail}>{form.user || "micuenta@yopmail.com"}</div>
          </div>
        </div>
        <div className={classes.imapCardToggle}>
          <StatusChip value={status} />
          <Switch
            color="primary"
            size="small"
            checked={form.enabled !== false}
            onChange={(e) => {
              setForm((f) => ({ ...f, enabled: e.target.checked }));
              if (form.id) onSave({ ...form, kind: "yopmail", enabled: e.target.checked });
            }}
          />
        </div>
      </div>
      <div className={classes.imapCardBody}>
        {form.lastError ? (
          <div className={classes.imapHint} style={{ color: "#c62828", marginTop: 0 }}>
            <InfoOutlinedIcon style={{ color: "#c62828" }} />
            {form.lastError}
          </div>
        ) : null}
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.codes.yopEmail")}
          placeholder="micuenta@yopmail.com"
          value={form.user || ""}
          onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))}
        />
        {details.length ? <div className={classes.imapDetails}>{details.join("\n")}</div> : null}
        <div className={classes.imapFooter}>
          <Button
            color="primary"
            variant="contained"
            className={classes.imapTestBtn}
            startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <WifiIcon />}
            onClick={testBox}
            disabled={testing || !form.user}
          >
            {testing ? tNegocio("pages.codes.testing") : tNegocio("pages.codes.test")}
          </Button>
          <span style={{ flex: 1 }} />
          <Button color="primary" variant="outlined" onClick={() => onSave({ ...form, kind: "yopmail" })} disabled={!form.user}>
            {tNegocio("save")}
          </Button>
          {form.id ? (
            <Tooltip title={tNegocio("pages.codes.removeMailbox")}>
              <IconButton onClick={() => onDelete(form)}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </div>
  );
}
